// One checkout for a camera group's locked cameras (components/shared/PayWall.js).
import { t } from "@/localization";
import {
  CAMERA_PRODUCT,
  DEFAULT_CAMERA_TIER,
  cameraTierOf,
  isCameraGroup,
} from "./multicam";
import { roomLabelOf } from "./rooms";

// Days an event still needs paying for, counted like the paywall does.
const daysDue = (event) =>
  event && !event.is_published && !event.deleted
    ? Math.max((Number(event.days) || 1) - (Number(event.days_paid) || 0), 1)
    : 0;

// The main camera if it's still locked, plus every camera that is. The main
// camera costs what any event does, at the tier the buyer picks; each extra
// camera is CAMERA_PRODUCT, whatever the tier. Units are days: `mainUnits`
// the main camera's, `cameraUnits` the extra cameras' added up. Null when
// it would be the event's own paywall: not a camera group, or no extra
// camera left to pay for.
export const cameraCheckoutOf = (primary, rooms = []) => {
  if (!primary?.slug || primary.room_of || !isCameraGroup(primary)) return null;
  const mainPaid = !!primary.is_published;
  const mainUnits = daysDue(primary);
  if (!mainPaid && !mainUnits) return null;
  const cameras = rooms.filter(
    (room) => room?.room_of === primary.slug && daysDue(room) > 0
  );
  if (!cameras.length) return null;
  const due = mainUnits ? [primary, ...cameras] : cameras;
  const cameraUnits = cameras.reduce((sum, event) => sum + daysDue(event), 0);
  return {
    mainPaid,
    slugs: due.map((event) => event.slug),
    labels: due.map(roomLabelOf),
    cameras: due.length,
    quantity: mainUnits + cameraUnits,
    mainUnits,
    cameraUnits,
    cameraSlugs: cameras.map((event) => event.slug),
    // Cameras are kept as long as the main camera: its replay tier and its
    // expiry date, once it's paid.
    tier: mainPaid ? cameraTierOf(primary) || DEFAULT_CAMERA_TIER : null,
    expiry: mainPaid ? primary.datetime_expiry || null : null,
  };
};

// What one tap buys (BuyContext purchase()), on the main camera with
// `rooms`: evt.live's checkout charges the main camera at `tier` and each
// camera at CAMERA_PRODUCT. The App Store and Google Play sell them as two
// purchases: the main camera's tier for the main camera, then CAMERA_PRODUCT
// for the cameras (`followUp`). With the main camera paid, only the second.
export const cameraPurchaseOf = (primary, checkout, tier) => {
  const cameras = {
    productId: CAMERA_PRODUCT,
    quantity: checkout.cameraUnits,
    unlockSlugs: checkout.cameraSlugs,
  };
  if (checkout.mainPaid) return { slug: primary.slug, rooms: true, ...cameras };
  return {
    slug: primary.slug,
    rooms: true,
    productId: tier,
    quantity: checkout.mainUnits,
    unlockSlugs: [primary.slug],
    followUp: cameras,
  };
};

// "Camera 2", "Camera 2 and Camera 3", "Camera 1, Camera 2 and Camera 3".
export const listLabels = (labels = []) =>
  labels.length <= 1
    ? labels[0] || ""
    : t("{list} and {last}", {
        list: labels.slice(0, -1).join(", "),
        last: labels[labels.length - 1],
      });
